import type { RuleGroupType, RuleType } from "react-querybuilder";
import { createEmptyConditionValue } from "./enhancedTaggingConditionValue";

export const COUNT_FIELD_PREFIX = "count:";
export const COUNT_EMPTY_FIELD = COUNT_FIELD_PREFIX;
export const COUNT_TARGET_PLACEHOLDER = "Please select what to count";
export const DEFAULT_COUNT_OPERATOR = "=";

const COUNT_OPERATOR_SYMBOLS: Record<string, string> = {
  eq: "=",
  neq: "≠",
  gt: ">",
  gte: ">=",
  lt: "<",
  lte: "<=",
  "≥": ">=",
  "≤": "<=",
};

const countTargetLabels = new Map<string, string>();

/** Remember display names from GET count-options so summaries can use them. */
export function rememberCountTargetLabels(
  entries: readonly { code: string; name: string }[],
): void {
  for (const entry of entries) {
    const code = entry.code.trim();
    const name = entry.name.trim();
    if (code && name) countTargetLabels.set(code, name);
  }
}
export const DEFAULT_DIMENSION_OPERATOR = "containsAny";

export const COUNT_TARGETS = [
  {
    code: "impacted_product",
    field: "count:impacted_product",
    label: "Impacted product",
  },
  {
    code: "impacted_document",
    field: "count:impacted_document",
    label: "Impacted document",
  },
  {
    code: "impacted_dimension",
    field: "count:impacted_dimension",
    label: "Impacted dimension",
  },
] as const;

export type CountTargetCode = (typeof COUNT_TARGETS)[number]["code"];
export type CountTargetField = (typeof COUNT_TARGETS)[number]["field"];

export const COUNT_OPERATORS = [
  { name: "eq", label: "=" },
  { name: "neq", label: "≠" },
  { name: "gt", label: ">" },
  { name: "gte", label: "≥" },
  { name: "lt", label: "<" },
  { name: "lte", label: "≤" },
] as const;

export type CountOperatorName = (typeof COUNT_OPERATORS)[number]["name"];

export type CountAwareRule = RuleType & { ruleType?: "count" };

export function isCountField(field: string | undefined): boolean {
  return String(field ?? "").startsWith(COUNT_FIELD_PREFIX);
}

export function isCountRule(rule: {
  field?: unknown;
  ruleType?: unknown;
  type?: unknown;
}): boolean {
  return (
    rule.ruleType === "count" ||
    rule.type === "count" ||
    isCountField(String(rule.field ?? ""))
  );
}

export function countCodeFromField(field: string | undefined): string {
  const trimmed = String(field ?? "").trim();
  if (!trimmed.startsWith(COUNT_FIELD_PREFIX)) return "";
  return trimmed.slice(COUNT_FIELD_PREFIX.length).trim();
}

export function countTargetFromField(field: string | undefined): {
  code: string;
  field: string;
  label: string;
} | null {
  const code = countCodeFromField(field);
  if (!code) return null;
  const known = COUNT_TARGETS.find((target) => target.code === code);
  return {
    code,
    field: `${COUNT_FIELD_PREFIX}${code}`,
    label: countTargetLabels.get(code) || known?.label || code,
  };
}

export function isCountTargetField(field: string | undefined): boolean {
  return countCodeFromField(field).length > 0;
}

export function isCountOperator(operator: string | undefined): boolean {
  return COUNT_OPERATORS.some((item) => item.name === operator);
}

export function canonicalCountOperator(operator: string | undefined): string {
  const trimmed = String(operator ?? "").trim();
  if (!trimmed) return DEFAULT_COUNT_OPERATOR;
  if (COUNT_OPERATOR_SYMBOLS[trimmed]) return COUNT_OPERATOR_SYMBOLS[trimmed];
  if (/^[<>=≠]+$/.test(trimmed)) return trimmed;
  return DEFAULT_COUNT_OPERATOR;
}

export function countOperatorLabel(operator: string | undefined): string {
  return canonicalCountOperator(operator);
}

/** Whole number of 0 or greater. Halfway values round to the higher number. */
export function normalizeCountNumber(value: unknown): number {
  const parsed =
    typeof value === "number" ? value : Number(String(value ?? "").trim());
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.round(parsed));
}

export function countNumberFromRuleValue(value: unknown): number {
  if (typeof value === "number" || typeof value === "string") {
    return normalizeCountNumber(value);
  }
  if (Array.isArray(value)) {
    const first = value[0];
    if (typeof first === "string" || typeof first === "number") {
      return normalizeCountNumber(first);
    }
    if (first && typeof first === "object") {
      const record = first as { code?: unknown; name?: unknown };
      return normalizeCountNumber(record.code ?? record.name ?? 0);
    }
    return 0;
  }
  if (value && typeof value === "object") {
    const record = value as { count?: unknown; code?: unknown };
    if (record.count != null) return normalizeCountNumber(record.count);
    if (record.code != null) return normalizeCountNumber(record.code);
  }
  return 0;
}

export function normalizeCountField(field: string | undefined): string {
  const trimmed = String(field ?? "").trim();
  if (!trimmed || trimmed === COUNT_EMPTY_FIELD) return COUNT_EMPTY_FIELD;
  const code = trimmed.startsWith(COUNT_FIELD_PREFIX)
    ? trimmed.slice(COUNT_FIELD_PREFIX.length).trim()
    : trimmed;
  if (!code || /\s/.test(code)) return COUNT_EMPTY_FIELD;
  return `${COUNT_FIELD_PREFIX}${code}`;
}

export function toCountRule(rule: RuleType): CountAwareRule {
  return {
    ...(rule.id ? { id: rule.id } : {}),
    field: COUNT_EMPTY_FIELD,
    operator: DEFAULT_COUNT_OPERATOR,
    value: 0,
    valueSource: "value",
    ruleType: "count",
  };
}

export function toDimensionRule(rule: RuleType): RuleType {
  return {
    ...(rule.id ? { id: rule.id } : {}),
    field: "",
    operator: DEFAULT_DIMENSION_OPERATOR,
    value: createEmptyConditionValue(),
    valueSource: "value",
  };
}

export function formatCountConditionText(rule: {
  field?: unknown;
  operator?: unknown;
  value?: unknown;
  type?: unknown;
  ruleType?: unknown;
}): string {
  const raw = String(rule.field ?? "").trim();
  const code =
    countCodeFromField(raw) ||
    (rule.type === "count" || rule.ruleType === "count" ? raw : "");
  if (!code) return "";
  const known = COUNT_TARGETS.find((target) => target.code === code);
  const label = countTargetLabels.get(code) || known?.label || code;
  return `Count of ${label} ${countOperatorLabel(String(rule.operator ?? ""))} ${countNumberFromRuleValue(rule.value)}`;
}

function isGroup(item: unknown): item is RuleGroupType {
  return Boolean(item && typeof item === "object" && "rules" in item);
}

export function getRuleAtPath(
  query: RuleGroupType,
  path: readonly number[],
): RuleType | null {
  let current: RuleGroupType | RuleType = query;
  for (let index = 0; index < path.length; index += 1) {
    if (!isGroup(current)) return null;
    const next = current.rules[path[index] ?? -1];
    if (!next) return null;
    if (index === path.length - 1) {
      return isGroup(next) ? null : (next as RuleType);
    }
    current = next as RuleGroupType;
  }
  return null;
}

export function replaceRuleAtPath(
  query: RuleGroupType,
  path: readonly number[],
  nextRule: RuleType,
): RuleGroupType {
  if (path.length === 0) return query;
  const [index, ...rest] = path;
  return {
    ...query,
    rules: query.rules.map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      if (rest.length === 0) return nextRule;
      if (!isGroup(item)) return item;
      return replaceRuleAtPath(item, rest, nextRule);
    }),
  };
}
